import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChemistNotesFeed from "@/components/admin/ChemistNotesFeed";
import { getChemistComments, addChemistComment } from "@/services/workflowService";

const NOTES_COLOR = "#7c3aed";

const ChemistCommentsTab = ({ chemistId }) => {
  const [comments, setComments]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [adding, setAdding]         = useState(false);
  const [error, setError]           = useState(null);

  const loadComments = useCallback(async () => {
    if (!chemistId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getChemistComments(chemistId);
      setComments(data ?? []);
    } catch {
      setError("Failed to load notes.");
    } finally {
      setLoading(false);
    }
  }, [chemistId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const handleAdd = async (text) => {
    setAdding(true);
    setError(null);
    try {
      await addChemistComment(chemistId, text);
      await loadComments();
    } catch {
      setError("Failed to post note. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ChatBubbleOutlineIcon sx={{ color: NOTES_COLOR, fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NOTES_COLOR }}>
          Notes
        </Typography>
        {comments.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
            ({comments.length})
          </Typography>
        )}
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && comments.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={26} sx={{ color: NOTES_COLOR }} />
        </Box>
      ) : (
        <ChemistNotesFeed notes={comments} onAdd={handleAdd} adding={adding} />
      )}
    </Box>
  );
};

export default ChemistCommentsTab;
